import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import { useTranslation } from '@/core/i18n/i18n';

export type ReopenTarget =
  | { type: 'definition'; versionId: number }
  | { type: 'fulfillment'; versionId: number }
  | null;

interface ReopenPlanDialogProps {
  open: boolean;
  onClose: () => void;
  isAnyLoading: boolean;
  target: ReopenTarget;

  reopenPlanVersion?: ((versionId: number, reason: string) => Promise<unknown> | void) | undefined;
  reopenFulfillmentVersion?:
    | ((versionId: number, reason: string) => Promise<unknown> | void)
    | undefined;
  reloadPage: () => void;
}

/**
 * Dialog to reopen a plan or fulfillment version.
 * Manages its own local state (reason input) to reduce parent re-renders / lag.
 */
const ReopenPlanDialog: React.FC<ReopenPlanDialogProps> = ({
  open,
  onClose,
  isAnyLoading,
  target,
  reopenPlanVersion,
  reopenFulfillmentVersion,
  reloadPage,
}) => {
  const { t } = useTranslation();
  const [reopenReason, setReopenReason] = React.useState('');

  React.useEffect(() => {
    if (!open) {
      setReopenReason('');
    }
  }, [open, target]);

  const handleConfirm = () => {
    if (!target) return;
    const reason = reopenReason.trim();
    if (!reason) return;

    let p: Promise<unknown> | void | undefined;
    if (target.type === 'definition' && reopenPlanVersion) {
      p = Promise.resolve(reopenPlanVersion(target.versionId, reason));
    }
    if (target.type === 'fulfillment' && reopenFulfillmentVersion) {
      p = Promise.resolve(reopenFulfillmentVersion(target.versionId, reason));
    }

    Promise.resolve(p)
      .then(() => {
        onClose();

        setReopenReason('');
        reloadPage();
      })
      .catch((err) => {
        console.error('Error reabriendo versión:', err);
      });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: 4,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 0, pt: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#333' }}>
          {t('table.confirmReopenTitle')}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 4, py: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Warning sx={{ fontSize: 64, color: '#29b6f6', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#333' }}>
            {target?.type === 'fulfillment'
              ? t('table.confirmReopenMessageSelfAssessment') ||
                '¿Desea reabrir el autoevaluación (cumplimiento)?'
              : t('table.confirmReopenMessageDefinition') ||
                '¿Desea reabrir la definición del plan anual?'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.6, maxWidth: '80%' }}>
            {t('table.confirmReopenWarning') ||
              'Al reabrir, la versión volverá al estado En Proceso para permitir nuevas modificaciones.'}
          </Typography>
          {/* Input motivo */}
          <Box sx={{ mt: 3, width: '100%' }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, mb: 1, textAlign: 'left', color: '#333' }}
            >
              {t('table.reopenReasonLabel')} <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <textarea
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              disabled={isAnyLoading}
              rows={4}
              placeholder={t('table.reopenReasonPlaceholder')}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 6,
                border: '1px solid #c4cdd5',
                fontFamily: 'inherit',
                fontSize: '0.9rem',
                resize: 'vertical',
                outline: 'none',
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
              }}
            />
            <Typography
              variant="caption"
              sx={{ mt: 0.5, color: reopenReason.trim() ? '#4caf50' : '#d32f2f' }}
            >
              {reopenReason.trim() ? '' : t('table.reopenReasonRequired')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 4, gap: 2 }}>
        <Button
          variant="outlined"
          size="large"
          disabled={isAnyLoading}
          onClick={onClose}
          sx={{
            borderColor: '#00BCE4',
            color: '#00BCE4',
            px: 3,
            py: 1,
            borderRadius: 20,
            textTransform: 'none',
            minWidth: 120,
            '&:hover': {
              borderColor: '#00acc1',
              bgcolor: 'rgba(0, 188, 212, 0.04)',
            },
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          size="large"
          disabled={isAnyLoading || !target || !reopenReason.trim()}
          onClick={handleConfirm}
          sx={{
            bgcolor: '#00BCE4',
            color: 'white',
            px: 3,
            py: 1,
            borderRadius: 20,
            textTransform: 'none',
            minWidth: 150,
            '&:hover': { bgcolor: '#0099b3' },
          }}
        >
          {t('table.confirmReopen')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(ReopenPlanDialog);
